import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useSupabaseQuery } from '../../hooks/useSupabase';
import { useCompanyAdminLayoutContext } from '../../components/company-admin/CompanyAdminLayout';
import {
  Plus,
  Edit2,
  Trash2,
  Users,
  Building2,
  X,
  User,
  Mail,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react';

interface DepartmentWithUsage {
  id: string;
  created_at: string;
  company_id: string;
  name: string;
  allocated_seats: number;
  usedSeats: number;
  usagePercentage: number;
  is_active: boolean;
  settings?: {
    maxLicenses?: number;
  };
  admin?: {
    fullName: string | null;
    email: string;
  } | null;
}

export function CompanyDepartments() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { setHeaderAction, setPageTitle } = useCompanyAdminLayoutContext();

  const [showModal, setShowModal] = useState(false);
  const [editingDept, setEditingDept] = useState<DepartmentWithUsage | null>(null);
  const [formData, setFormData] = useState({
    name: '',
  });

  // Get departments
  const { data: rawDepartments, refetch } = useSupabaseQuery('departments', {
    filter: (q: any) => q.eq('company_id', profile?.companyId),
    enabled: !!profile?.companyId,
  });

  // Get profiles for usage calculation and admin info
  const { data: profilesData } = useSupabaseQuery('profiles', {
    select: 'id, department_id, is_active, role, full_name, email',
    filter: (q: any) => q.eq('company_id', profile?.companyId),
    enabled: !!profile?.companyId,
  });

  // Build departments with usage data
  const departments: DepartmentWithUsage[] | undefined = rawDepartments?.map((dept: any) => {
    const deptProfiles = (profilesData || []).filter((p: any) => p.department_id === dept.id);
    const activeProfiles = deptProfiles.filter((p: any) => p.is_active !== false);
    const usedSeats = activeProfiles.length;
    const allocatedSeats = dept.allocated_seats || 0;
    const usagePercentage = allocatedSeats > 0 ? Math.round((usedSeats / allocatedSeats) * 100) : 0;
    const adminProfile = deptProfiles.find((p: any) => p.role === 'dept_admin');

    return {
      id: dept.id,
      created_at: dept.created_at,
      company_id: dept.company_id,
      name: dept.name,
      allocated_seats: allocatedSeats,
      usedSeats,
      usagePercentage,
      is_active: dept.is_active !== false,
      settings: dept.settings,
      admin: adminProfile ? {
        fullName: adminProfile.full_name,
        email: adminProfile.email,
      } : null,
    };
  });

  // Set page title and header action
  useEffect(() => {
    setPageTitle('Avdelinger');
    setHeaderAction(
      <button
        onClick={() => {
          setFormData({ name: '' });
          setEditingDept(null);
          setShowModal(true);
        }}
        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
      >
        <Plus className="w-5 h-5" />
        Ny avdeling
      </button>
    );
    return () => {
      setPageTitle(null);
      setHeaderAction(null);
    };
  }, [setPageTitle, setHeaderAction]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.companyId) return;

    try {
      if (editingDept) {
        const { error } = await supabase
          .from('departments')
          .update({ name: formData.name })
          .eq('id', editingDept.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('departments')
          .insert({
            company_id: profile.companyId,
            name: formData.name,
          });
        if (error) throw error;
      }
      handleCancel();
      refetch();
    } catch (error: any) {
      console.error('Error saving department:', error);
      alert(error.message || 'Kunne ikke lagre avdeling');
    }
  };

  const handleEdit = (dept: DepartmentWithUsage) => {
    setEditingDept(dept);
    setFormData({
      name: dept.name,
    });
    setShowModal(true);
  };

  const handleDelete = async (dept: DepartmentWithUsage) => {
    const userCount = dept.usedSeats || 0;
    if (userCount > 0) {
      alert(`Kan ikke slette avdeling med ${userCount} aktive brukere. Flytt eller slett brukerne først.`);
      return;
    }

    if (!window.confirm(`Er du sikker på at du vil slette "${dept.name}"?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('departments')
        .delete()
        .eq('id', dept.id);
      if (error) throw error;
      refetch();
    } catch (error: any) {
      console.error('Error deleting department:', error);
      alert(error.message || 'Kunne ikke slette avdeling');
    }
  };

  const handleCancel = () => {
    setShowModal(false);
    setEditingDept(null);
    setFormData({ name: '' });
  };

  if (departments === undefined) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600 dark:text-gray-400">Laster avdelinger...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {departments.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">Ingen avdelinger ennå</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
            Opprett din første avdeling for å organisere brukere
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b dark:border-gray-600">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Avdeling
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Avdelingsadmin
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Antall lisenser
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Handlinger
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {departments.map((dept) => (
                <tr key={dept.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div
                      className="flex items-center gap-3 cursor-pointer hover:opacity-75"
                      onClick={() => navigate(`/company-admin/users?department_id=${dept.id}&department_name=${encodeURIComponent(dept.name)}`)}
                    >
                      <div className="h-10 w-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Building2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                      </div>
                      <div>
                        <div className="font-medium text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300">
                          {dept.name}
                        </div>
                        {!dept.is_active && (
                          <div className="flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                            <AlertTriangle className="h-3 w-3" />
                            Inaktiv
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {dept.admin ? (
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                          <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 dark:text-white truncate">
                            {dept.admin.fullName || 'Ikke satt'}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                            <Mail className="h-3 w-3" />
                            <span className="truncate">{dept.admin.email}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500 text-sm">Ingen admin</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="w-full max-w-xs">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-gray-600 dark:text-gray-400">
                          {dept.usedSeats || 0} / {dept.allocated_seats || 0}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {dept.usagePercentage || 0}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            (dept.usagePercentage || 0) >= 100
                              ? 'bg-red-500'
                              : (dept.usagePercentage || 0) >= 75
                              ? 'bg-orange-500'
                              : 'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(dept.usagePercentage || 0, 100)}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(dept);
                        }}
                        className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                        title="Rediger avdeling"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(dept);
                        }}
                        className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                        title="Slett avdeling"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && createPortal(
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-[9999]">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {editingDept ? 'Rediger avdeling' : 'Ny avdeling'}
              </h2>
              <button
                onClick={handleCancel}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Avdelingsnavn *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <p className="text-sm text-gray-500 dark:text-gray-400">
                Antall plasser for avdelingen settes av systemadministrator.
              </p>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Avbryt
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingDept ? 'Oppdater' : 'Opprett'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
